import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { FiUser, FiMail, FiLock, FiBarChart2, FiCheckCircle, FiXCircle, FiSend, FiTrendingUp, FiBell, FiStar } from 'react-icons/fi';

const AnimatedNumber = ({ value, duration = 1200 }) => {
  const [display, setDisplay] = useState(0);
  const ref = useRef();
  useEffect(() => {
    const num = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.]/g, '')) : (value || 0);
    const suffix = typeof value === 'string' && value.includes('%') ? '%' : '';
    if (isNaN(num)) { setDisplay(value); return; }
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setDisplay(suffix ? `${Math.round(num * ease)}%` : Math.round(num * ease).toLocaleString());
      if (p < 1) ref.current = requestAnimationFrame(step);
    };
    ref.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(ref.current);
  }, [value, duration]);
  return <>{display}</>;
};

export default function UserProfile() {
  const { user, updateProfile } = useAuth();
  const [form, setForm] = useState({ email: user?.email || '' });
  const [passForm, setPassForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [saving, setSaving] = useState(false);
  const [changingPass, setChangingPass] = useState(false);
  const [stats, setStats] = useState(null);
  const [notifPrefs, setNotifPrefs] = useState({ campaignAlerts: true, failureAlerts: true, weeklyDigest: false });

  useEffect(() => {
    api.get('/user/profile-stats').then(({ data }) => setStats(data)).catch(() => {});
    // Fetch notification preferences from database
    api.get('/auth/notification-prefs').then(({ data }) => {
      setNotifPrefs({
        campaignAlerts: data.campaignAlerts ?? true,
        failureAlerts: data.failureAlerts ?? true,
        weeklyDigest: data.weeklyDigest ?? false,
      });
    }).catch(() => {});
  }, []);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateProfile({ email: form.email });
      toast.success('Profile updated');
    } catch (err) { toast.error(err.response?.data?.msg || 'Update failed'); }
    finally { setSaving(false); }
  };

  const handlePassword = async (e) => {
    e.preventDefault();
    if (passForm.newPassword !== passForm.confirmPassword) { toast.error('Passwords do not match'); return; }
    if (passForm.newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setChangingPass(true);
    try {
      await updateProfile({ currentPassword: passForm.currentPassword, newPassword: passForm.newPassword });
      toast.success('Password changed');
      setPassForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) { toast.error(err.response?.data?.msg || 'Password change failed'); }
    finally { setChangingPass(false); }
  };

  const toggleNotif = async (key) => {
    const prev = { ...notifPrefs };
    const updated = { ...notifPrefs, [key]: !notifPrefs[key] };
    setNotifPrefs(updated);
    try {
      await api.put('/auth/notification-prefs', { [key]: updated[key] });
      toast.success('Preference updated');
    } catch {
      setNotifPrefs(prev);
      toast.error('Failed to save preference');
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">My Account</h1>
          <p className="page-subtitle">Manage your profile and preferences</p>
        </div>
      </div>

      {/* Profile Card */}
      <div className="card" style={{ marginBottom: 20, padding: '28px 32px', display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap', animationDelay: '0.05s' }}>
        <div style={{
          width: 88, height: 88, borderRadius: '50%',
          background: 'linear-gradient(135deg, #7ea52e, #1a1a18)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 32, fontWeight: 800, color: '#fff',
          boxShadow: '0 8px 24px rgba(126,165,46,0.25)',
          animation: 'popIn 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.2s both',
          transition: 'transform 0.4s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.4s',
        }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.08) rotate(3deg)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(126,165,46,0.35)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 8px 24px rgba(126,165,46,0.25)'; }}>
          {user?.username?.charAt(0).toUpperCase()}
        </div>
        <div style={{ animation: 'slideInUp 0.4s cubic-bezier(0.22, 1, 0.36, 1) 0.25s both' }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', fontFamily: 'Poppins, sans-serif' }}>{user?.username}</div>
          <div style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 2 }}>{user?.email || 'No email set'}</div>
          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <span className="badge badge-info" style={{ fontSize: 11 }}>{user?.role}</span>
            {stats && <span className="badge badge-success" style={{ fontSize: 11 }}>{stats.successRate}% Success Rate</span>}
          </div>
        </div>
        {stats && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 20 }}>
            {[
              { icon: <FiSend size={16} />, value: stats.totalCampaigns, label: 'Campaigns', color: '#7ea52e' },
              { icon: <FiCheckCircle size={16} />, value: stats.totalSent, label: 'Sent', color: '#7ea52e' },
              { icon: <FiXCircle size={16} />, value: stats.totalFailed, label: 'Failed', color: '#1a1a18' },
            ].map((s, i) => (
              <div key={i} style={{
                textAlign: 'center',
                animation: `popIn 0.4s cubic-bezier(0.22, 1, 0.36, 1) ${0.3 + i * 0.1}s both`,
                transition: 'transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
              }}
                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1) translateY(-2px)'}
                onMouseLeave={e => e.currentTarget.style.transform = ''}>
                <div style={{ color: s.color, marginBottom: 4 }}>{s.icon}</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)' }}><AnimatedNumber value={s.value} duration={1000 + i * 200} /></div>
                <div style={{ fontSize: 10, fontWeight: 600, color: '#abab9f', textTransform: 'uppercase' }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="stats-grid" style={{ marginBottom: 20 }}>
          {[
            { icon: <FiBarChart2 />, value: stats.activeCampaigns, label: 'Active Campaigns', cls: 'blue', accent: '#7ea52e' },
            { icon: <FiStar />, value: stats.completedCampaigns, label: 'Completed', cls: 'green', accent: '#5c7a1f' },
            { icon: <FiTrendingUp />, value: `${stats.successRate}%`, label: 'Success Rate', cls: 'purple', accent: '#1a1a18' },
            { icon: <FiBell />, value: stats.recentActivityCount, label: 'This Week Activity', cls: 'yellow', accent: '#9ec25a' },
          ].map((s, i) => (
            <div key={i} className="stat-card" style={{ borderBottom: `3px solid ${s.accent}`, animationDelay: `${0.08 + i * 0.06}s` }}
              onMouseMove={e => { const r = e.currentTarget.getBoundingClientRect(); e.currentTarget.style.setProperty('--mouse-x', `${e.clientX - r.left}px`); e.currentTarget.style.setProperty('--mouse-y', `${e.clientY - r.top}px`); }}>
              <div className={`stat-card-icon ${s.cls}`}>{s.icon}</div>
              <div className="stat-card-value"><AnimatedNumber value={s.value} duration={1000 + i * 200} /></div>
              <div className="stat-card-label">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Account Info */}
        <div className="card" style={{ animationDelay: '0.15s' }}>
          <h3 className="card-title" style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiMail size={16} color="#7ea52e" /> Account Info
          </h3>
          <form onSubmit={handleUpdate}>
            <div className="form-group">
              <label className="form-label">Username</label>
              <input type="text" className="form-input" value={user?.username || ''} disabled style={{ opacity: 0.6 }} />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input type="email" className="form-input" value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })} />
            </div>
            <button type="submit" className="btn btn-primary" disabled={saving} style={{ width: '100%' }}>
              {saving ? 'Saving...' : 'Update Profile'}
            </button>
          </form>
        </div>

        {/* Change Password */}
        <div className="card" style={{ animationDelay: '0.22s' }}>
          <h3 className="card-title" style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiLock size={16} color="#1a1a18" /> Change Password
          </h3>
          <form onSubmit={handlePassword}>
            <div className="form-group">
              <label className="form-label">Current Password</label>
              <input type="password" className="form-input" value={passForm.currentPassword}
                onChange={e => setPassForm({ ...passForm, currentPassword: e.target.value })} required />
            </div>
            <div className="form-group">
              <label className="form-label">New Password</label>
              <input type="password" className="form-input" value={passForm.newPassword}
                onChange={e => setPassForm({ ...passForm, newPassword: e.target.value })} required />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm New Password</label>
              <input type="password" className="form-input" value={passForm.confirmPassword}
                onChange={e => setPassForm({ ...passForm, confirmPassword: e.target.value })} required />
            </div>
            <button type="submit" className="btn btn-primary" disabled={changingPass} style={{ width: '100%' }}>
              {changingPass ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        </div>
      </div>

      {/* Notification Preferences */}
      <div className="card" style={{ marginBottom: 20, animationDelay: '0.28s' }}>
        <h3 className="card-title" style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
          <FiBell size={16} color="#9ec25a" /> Notification Preferences
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {[
            { key: 'campaignAlerts', label: 'Campaign Alerts', desc: 'Get notified when new campaigns are assigned to you' },
            { key: 'failureAlerts', label: 'Failure Alerts', desc: 'Receive alerts when messages fail delivery' },
            { key: 'weeklyDigest', label: 'Weekly Digest', desc: 'Receive weekly summary of your performance' },
          ].map((pref, i) => (
            <div key={pref.key} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 16px', borderRadius: 10,
              background: 'var(--bg-secondary)', border: '1px solid var(--border-color)',
              animation: `revealUp 0.4s cubic-bezier(0.22, 1, 0.36, 1) ${0.3 + i * 0.08}s both`,
              transition: 'transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.3s',
            }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateX(4px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(126,165,46,0.08)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{pref.label}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{pref.desc}</div>
              </div>
              <button onClick={() => toggleNotif(pref.key)}
                style={{
                  width: 48, height: 26, borderRadius: 13, border: 'none', cursor: 'pointer', position: 'relative',
                  background: notifPrefs[pref.key] ? '#7ea52e' : '#abab9f',
                  transition: 'background 0.35s cubic-bezier(0.22, 1, 0.36, 1), transform 0.2s',
                  boxShadow: notifPrefs[pref.key] ? '0 2px 8px rgba(126,165,46,0.3)' : 'none',
                }}
                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.08)'}
                onMouseLeave={e => e.currentTarget.style.transform = ''}>
                <span style={{
                  position: 'absolute', top: 3, left: notifPrefs[pref.key] ? 25 : 3,
                  width: 20, height: 20, borderRadius: '50%', background: '#fff',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
                  transition: 'left 0.35s cubic-bezier(0.22, 1, 0.36, 1)',
                }} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Channel Types */}
      {stats?.channelTypes?.length > 0 && (
        <div className="card">
          <h3 className="card-title" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiUser size={16} color="#7ea52e" /> My Channels
          </h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {stats.channelTypes.map(type => (
              <span key={type} className="badge badge-info" style={{ padding: '6px 14px', fontSize: 12, borderRadius: 8 }}>
                {type?.replace('_', ' ')}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
